import React, { useState, useEffect } from 'react';
import ModalWrapper from './ModalWrapper';

const AddInvoiceModal = ({ isOpen, onClose, onSubmit, type = 'invoice', eventId, categoryId, isVendor = false, vendorEvents = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    vendor: '',
    categoryId: categoryId || '',
    selectedEventId: eventId || '',
    description: '',
    lineItems: [{ description: '', units: 1, amount: '' }],
    totalAmount: '',
    depositAmount: '',
    depositDue: '',
    dueDate: '',
    notes: '',
    pdfFile: null
  });
  const [vendors, setVendors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isEventDropdownOpen, setIsEventDropdownOpen] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [showAddVendor, setShowAddVendor] = useState(false);

  const activeEventId = isVendor ? formData.selectedEventId : eventId;

  useEffect(() => {
    const loadData = async () => {
      if (!activeEventId) return;
      try {
        if (!isVendor) {
          const vendorResponse = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${activeEventId}/vendors`);
          if (vendorResponse.ok) {
            const vendorAssociations = await vendorResponse.json();
            const vendorDetails = [];
            for (const association of vendorAssociations) {
              const vResponse = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${association.vendorId}`);
              if (vResponse.ok) {
                const vendorData = await vResponse.json();
                const vendor = {};
                for (const [key, value] of Object.entries(vendorData)) {
                  if (value?.S !== undefined) vendor[key] = value.S;
                  else if (value?.N !== undefined) vendor[key] = parseInt(value.N);
                  else if (value?.BOOL !== undefined) vendor[key] = value.BOOL;
                  else vendor[key] = value;
                }
                vendorDetails.push(vendor);
              }
            }
            setVendors(vendorDetails);
          }
        }

        const catResponse = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${activeEventId}/categories`);
        if (catResponse.ok) {
          const cats = await catResponse.json();
          setCategories(cats);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    if (isOpen) {
      loadData();
      if (categoryId) setFormData(prev => ({ ...prev, categoryId }));
    }
  }, [isOpen, activeEventId, categoryId, isVendor]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploadError('');
    setIsUploading(true);
    try {
      await onSubmit({ ...formData, eventId: activeEventId });
    } catch (error) {
      setUploadError(error.message || 'Failed to submit. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const addLineItem = () => {
    setFormData({ ...formData, lineItems: [...formData.lineItems, { description: '', units: 1, amount: '' }] });
  };

  const removeLineItem = (index) => {
    if (formData.lineItems.length <= 1) return;
    setFormData({ ...formData, lineItems: formData.lineItems.filter((_, i) => i !== index) });
  };

  const updateLineItem = (index, field, value) => {
    const updatedItems = formData.lineItems.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    setFormData({ ...formData, lineItems: updatedItems });
  };

  const lineItemsTotal = formData.lineItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0) * (parseInt(item.units) || 1), 0);

  if (!isOpen) return null;

  const inputStyle = {
    width: '100%',
    padding: '12px 16px',
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    fontSize: '16px',
    backgroundColor: '#F9FAFB'
  };

  const labelStyle = {
    display: 'block',
    fontSize: '14px',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '6px'
  };

  return (
    <ModalWrapper isOpen={isOpen} onClose={onClose} maxWidth="600px">
      <h2 style={{
        fontSize: '32px',
        fontWeight: '400',
        color: '#1F2937',
        marginBottom: '24px',
        fontFamily: 'Georgia, serif'
      }}>
        Add {type === 'quote' ? 'Quote' : 'Invoice'}
      </h2>

      <form onSubmit={handleSubmit}>
        {/* Event Selector (vendor context) */}
        {isVendor && vendorEvents.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Event *</label>
            <div style={{ position: 'relative' }}>
              <div
                onClick={() => setIsEventDropdownOpen(!isEventDropdownOpen)}
                style={{
                  ...inputStyle,
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span>{formData.selectedEventId ? vendorEvents.find(e => e.id === formData.selectedEventId)?.name || 'Select event...' : 'Select an event...'}</span>
                <span style={{ color: '#6B7280' }}>▼</span>
              </div>
              {isEventDropdownOpen && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0,
                  backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px',
                  marginTop: '4px', zIndex: 1000, maxHeight: '200px', overflowY: 'auto'
                }}>
                  {vendorEvents.map(event => (
                    <div
                      key={event.id}
                      onClick={() => { setFormData({ ...formData, selectedEventId: event.id }); setIsEventDropdownOpen(false); }}
                      style={{ padding: '12px 16px', cursor: 'pointer', color: 'black' }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#E8F4E8'}
                      onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      {event.name} — {new Date(event.eventDate).toLocaleDateString()}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Invoice/Quote Name */}
        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>{type === 'quote' ? 'Quote' : 'Invoice'} Name *</label>
          <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} style={inputStyle} />
        </div>

        {/* Description */}
        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={2}
            placeholder="Brief description of this invoice/quote..."
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>

        {/* Vendor (only for client context) */}
        {!isVendor && (
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Vendor</label>
            <div style={{ position: 'relative' }}>
              <div
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                style={{
                  ...inputStyle,
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <span>{formData.vendor ? vendors.find(v => v.id === formData.vendor)?.name : 'Select a vendor...'}</span>
                <span style={{ color: '#6B7280' }}>▼</span>
              </div>
              {isDropdownOpen && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0,
                  backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '8px',
                  marginTop: '4px', zIndex: 1000, maxHeight: '200px', overflowY: 'auto'
                }}>
                  <div
                    onClick={() => { setFormData({ ...formData, vendor: '' }); setIsDropdownOpen(false); }}
                    style={{ padding: '12px 16px', cursor: 'pointer', color: '#6B7280' }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#6B9080'}
                    onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
                  >
                    Select a vendor...
                  </div>
                  {vendors.map(vendor => (
                    <div
                      key={vendor.id}
                      onClick={() => { setFormData({ ...formData, vendor: vendor.id }); setIsDropdownOpen(false); }}
                      style={{ padding: '12px 16px', cursor: 'pointer', color: 'black' }}
                      onMouseOver={(e) => e.target.style.backgroundColor = '#6B9080'}
                      onMouseOut={(e) => e.target.style.backgroundColor = 'white'}
                    >
                      {vendor.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
              Can't find your vendor? <span onClick={() => setShowAddVendor(true)} style={{ color: '#6B9080', cursor: 'pointer', textDecoration: 'underline' }}>Click here</span> to add them.
            </div>
          </div>
        )}

        {/* Budget Group */}
        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Budget Group</label>
          <select value={formData.categoryId} onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })} style={{ ...inputStyle, cursor: 'pointer' }}>
            <option value="">Select category...</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>

        {/* Line Items */}
        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Line Items</label>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 80px 100px 32px', gap: '8px', marginBottom: '8px' }}>
            <div style={{ fontSize: '12px', color: '#6B7280' }}>Description</div>
            <div style={{ fontSize: '12px', color: '#6B7280' }}>Units</div>
            <div style={{ fontSize: '12px', color: '#6B7280' }}>Amount</div>
            <div></div>
          </div>
          {formData.lineItems.map((item, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '2fr 80px 100px 32px', gap: '8px', marginBottom: '8px' }}>
              <input type="text" value={item.description} onChange={(e) => updateLineItem(i, 'description', e.target.value)} style={{ ...inputStyle, padding: '8px 12px', fontSize: '14px' }} />
              <input type="number" min="1" value={item.units} onChange={(e) => updateLineItem(i, 'units', e.target.value)} style={{ ...inputStyle, padding: '8px 12px', fontSize: '14px' }} />
              <input type="number" min="0" step="0.01" value={item.amount} onChange={(e) => updateLineItem(i, 'amount', e.target.value)} style={{ ...inputStyle, padding: '8px 12px', fontSize: '14px' }} />
              <button type="button" onClick={() => removeLineItem(i)} style={{ background: 'none', border: 'none', color: '#DC2626', cursor: 'pointer', fontSize: '18px' }}>×</button>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <button type="button" onClick={addLineItem} style={{ padding: '6px 14px', backgroundColor: '#E8F4E8', color: '#6B9080', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>+ Add Item</button>
            {lineItemsTotal > 0 && <div style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937' }}>Total: ${lineItemsTotal.toFixed(2)}</div>}
          </div>
        </div>

        {/* Total Amount */}
        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Total Amount *</label>
          <input type="number" min="0" step="0.01" value={formData.totalAmount} onChange={(e) => setFormData({ ...formData, totalAmount: e.target.value })} style={inputStyle} placeholder="Full invoice/quote amount" />
        </div>

        {/* Deposit Amount & Due Date row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '20px' }}>
          <div>
            <label style={labelStyle}>Deposit Amount</label>
            <input type="number" min="0" step="0.01" value={formData.depositAmount} onChange={(e) => setFormData({ ...formData, depositAmount: e.target.value })} style={inputStyle} placeholder="If different from total" />
          </div>
          <div>
            <label style={labelStyle}>Due Date</label>
            <input type="date" value={formData.dueDate} onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })} style={{ ...inputStyle, cursor: 'pointer', colorScheme: 'light' }} />
          </div>
        </div>

        {/* Notes */}
        <div style={{ marginBottom: '20px' }}>
          <label style={labelStyle}>Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={2}
            placeholder="Any additional notes or terms..."
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>

        {/* Upload Document */}
        <div style={{ marginBottom: '32px' }}>
          <label style={labelStyle}>Upload Document</label>
          <div style={{ position: 'relative' }}>
            <input
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  if (file.size > 10 * 1024 * 1024) { setUploadError('File size must be less than 10MB'); return; }
                  setUploadError('');
                  setFormData({ ...formData, pdfFile: file });
                }
              }}
              style={{ position: 'absolute', opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
              id="file-upload"
            />
            <label htmlFor="file-upload" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: '100%', padding: '12px 16px', border: '1px solid #E5E7EB', borderRadius: '8px',
              fontSize: '16px', backgroundColor: '#F9FAFB', cursor: 'pointer', color: '#6B7280'
            }}>
              <svg style={{ width: '20px', height: '20px', marginRight: '8px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              {formData.pdfFile ? formData.pdfFile.name : 'Choose file (PDF, DOC, DOCX, JPG, PNG)'}
            </label>
          </div>
          {uploadError && <div style={{ color: '#DC2626', fontSize: '14px', marginTop: '4px' }}>{uploadError}</div>}
        </div>

        {/* Add Vendor Modal (client only) */}
        {showAddVendor && !isVendor && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000
          }}>
            <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '32px', width: '400px' }}>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937', marginBottom: '16px' }}>Add New Vendor</h3>
              <input type="text" placeholder="Vendor Name" id="newVendorName" style={{ ...inputStyle, marginBottom: '12px' }} />
              <input type="email" placeholder="Vendor Email" id="newVendorEmail" style={{ ...inputStyle, marginBottom: '16px' }} />
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowAddVendor(false)} style={{ padding: '12px 24px', backgroundColor: 'white', color: '#6B7280', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>Cancel</button>
                <button type="button" onClick={async (e) => {
                  e.preventDefault(); e.stopPropagation();
                  const name = document.getElementById('newVendorName').value;
                  const email = document.getElementById('newVendorEmail').value;
                  if (!name || !email) return;
                  try {
                    const response = await fetch('https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors', {
                      method: 'POST', headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ name, email, vendorType: 'other' })
                    });
                    if (response.ok) {
                      const data = await response.json();
                      await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${activeEventId}/vendors`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ vendorId: data.vendorId })
                      });
                      const vendorResponse = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/events/${activeEventId}/vendors`);
                      if (vendorResponse.ok) {
                        const vendorAssociations = await vendorResponse.json();
                        const vendorDetails = [];
                        for (const association of vendorAssociations) {
                          const vResponse = await fetch(`https://etz8ecs10a.execute-api.ap-southeast-2.amazonaws.com/prod/api/vendors/${association.vendorId}`);
                          if (vResponse.ok) {
                            const vendorData = await vResponse.json();
                            const vendor = {};
                            for (const [key, value] of Object.entries(vendorData)) {
                              if (value?.S !== undefined) vendor[key] = value.S;
                              else if (value?.N !== undefined) vendor[key] = parseInt(value.N);
                              else if (value?.BOOL !== undefined) vendor[key] = value.BOOL;
                              else vendor[key] = value;
                            }
                            vendorDetails.push(vendor);
                          }
                        }
                        setVendors(vendorDetails);
                        setFormData({ ...formData, vendor: data.vendorId });
                      }
                      alert(`Vendor added! Let ${name} know to check their email at ${email} for an onboarding link.`);
                      setShowAddVendor(false);
                    }
                  } catch (error) {
                    console.error('Error adding vendor:', error);
                    alert('Failed to add vendor. Please try again.');
                  }
                }} style={{ padding: '12px 24px', backgroundColor: '#6B9080', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>Add Vendor</button>
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button type="button" onClick={onClose} style={{ padding: '12px 24px', backgroundColor: 'white', color: '#6B7280', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>Cancel</button>
          <button type="submit" disabled={isUploading} style={{
            padding: '12px 24px',
            backgroundColor: isUploading ? '#9CA3AF' : '#6B9080',
            color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '500',
            cursor: isUploading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '8px'
          }}>
            {isUploading && <div style={{ width: '16px', height: '16px', border: '2px solid transparent', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>}
            {isUploading ? 'Uploading...' : `Add ${type === 'quote' ? 'Quote' : 'Invoice'}`}
          </button>
        </div>
      </form>

      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </ModalWrapper>
  );
};

export default AddInvoiceModal;
